import { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { toast } from "sonner";
import {
  Building2,
  User,
  ChevronDown,
  ChevronRight,
  Search,
  Save,
  Mail,
  MessageSquare,
  Bell,
  Zap,
  Activity,
  ShieldAlert,
  Settings,
  Power,
  PlugZap,
} from "lucide-react";
import Layout from "../../layout";

// ── Event definitions based on DLMS/COSEM smart meter spec ──

interface MeterEvent {
  id: number;
  name: string;
  defaultEnabled: boolean;
}

interface EventCategory {
  code: number;
  label: string;
  icon: React.ReactNode;
  events: MeterEvent[];
}

const eventCategories: EventCategory[] = [
  {
    code: 21,
    label: "Voltage Related Events",
    icon: <Zap className="w-4 h-4" />,
    events: [
      { id: 7, name: "Over Voltage – Occurrence", defaultEnabled: true },
      { id: 8, name: "Over Voltage – Restoration", defaultEnabled: true },
      { id: 9, name: "Low Voltage – Occurrence", defaultEnabled: true },
      { id: 10, name: "Low Voltage – Restoration", defaultEnabled: true },
    ],
  },
  {
    code: 22,
    label: "Current Related Events",
    icon: <Activity className="w-4 h-4" />,
    events: [
      { id: 69, name: "Earth Loading – Occurrence", defaultEnabled: true },
      { id: 70, name: "Earth Loading – Restoration", defaultEnabled: false },
      { id: 67, name: "Over Current – Occurrence", defaultEnabled: true },
      { id: 68, name: "Over Current – Restoration", defaultEnabled: false },
      { id: 51, name: "Current Reverse – Occurrence", defaultEnabled: true },
      { id: 52, name: "Current Reverse – Restoration", defaultEnabled: false },
    ],
  },
  {
    code: 23,
    label: "Power Related Events",
    icon: <Power className="w-4 h-4" />,
    events: [
      { id: 101, name: "Power Failure – Occurrence", defaultEnabled: true },
      { id: 102, name: "Power Failure – Restoration", defaultEnabled: true },
    ],
  },
  {
    code: 24,
    label: "Transaction Related Events",
    icon: <Settings className="w-4 h-4" />,
    events: [
      { id: 157, name: "New Firmware Activated", defaultEnabled: true },
      { id: 158, name: "Load Limit kW Set", defaultEnabled: true },
      { id: 159, name: "Enable – Load Limit Function", defaultEnabled: true },
      { id: 160, name: "Disable – Load Limit Function", defaultEnabled: true },
      { id: 166, name: "MD Reset", defaultEnabled: true },
    ],
  },
  {
    code: 25,
    label: "Other Events",
    icon: <ShieldAlert className="w-4 h-4" />,
    events: [
      {
        id: 201,
        name: "Abnormal External Magnet Influence – Occurrence",
        defaultEnabled: true,
      },
      {
        id: 202,
        name: "Abnormal External Magnet Influence – Restoration",
        defaultEnabled: false,
      },
      {
        id: 203,
        name: "Neutral Disturbance – Occurrence",
        defaultEnabled: true,
      },
      {
        id: 204,
        name: "Neutral Disturbance – Restoration",
        defaultEnabled: false,
      },
      {
        id: 207,
        name: "Single Wire Operation (Neutral Missing) – Occurrence",
        defaultEnabled: true,
      },
      {
        id: 208,
        name: "Single Wire Operation (Neutral Missing) – Restoration",
        defaultEnabled: false,
      },
      {
        id: 209,
        name: "Plug-in Communication Module Removal – Occurrence",
        defaultEnabled: true,
      },
      {
        id: 210,
        name: "Plug-in Communication Module Removal – Restoration",
        defaultEnabled: false,
      },
      {
        id: 211,
        name: "Configuration Change to Postpaid Mode",
        defaultEnabled: true,
      },
      {
        id: 212,
        name: "Configuration Change to Prepaid Mode",
        defaultEnabled: true,
      },
      {
        id: 213,
        name: "Configuration Change to Forward Only Mode",
        defaultEnabled: false,
      },
      {
        id: 214,
        name: "Configuration Change to Import-Export Mode",
        defaultEnabled: false,
      },
      { id: 215, name: "Overload – Occurrence", defaultEnabled: true },
      { id: 216, name: "Overload – Restoration", defaultEnabled: false },
    ],
  },
  {
    code: 26,
    label: "Non-Rollover Events",
    icon: <ShieldAlert className="w-4 h-4" />,
    events: [
      {
        id: 251,
        name: "Meter Cover Opening – Occurrence",
        defaultEnabled: true,
      },
    ],
  },
  {
    code: 27,
    label: "Control Events (Connect / Disconnect)",
    icon: <PlugZap className="w-4 h-4" />,
    events: [
      {
        id: 301,
        name: "Load Switch Status – Disconnected",
        defaultEnabled: true,
      },
      { id: 302, name: "Load Switch Status – Connected", defaultEnabled: true },
    ],
  },
];

type ChannelKey = "sms" | "email";

interface EventNotifState {
  enabled: boolean;
  channels: Record<ChannelKey, boolean>;
}

const defaultEventState = (evt: MeterEvent): EventNotifState => ({
  enabled: evt.defaultEnabled,
  channels: { sms: evt.defaultEnabled, email: evt.defaultEnabled },
});

const buildInitialState = () => {
  const state: Record<number, EventNotifState> = {};
  eventCategories.forEach((cat) =>
    cat.events.forEach((evt) => {
      state[evt.id] = defaultEventState(evt);
    }),
  );
  return state;
};

// ── Reusable event config table ──

const EventConfigSection = ({
  state,
  setState,
}: {
  state: Record<number, EventNotifState>;
  setState: React.Dispatch<
    React.SetStateAction<Record<number, EventNotifState>>
  >;
}) => {
  const [openCats, setOpenCats] = useState<Record<number, boolean>>({});

  const toggleCat = (code: number) =>
    setOpenCats((p) => ({ ...p, [code]: !p[code] }));

  const toggleEvent = (id: number) =>
    setState((p) => ({
      ...p,
      [id]: { ...p[id], enabled: !p[id].enabled },
    }));

  const toggleChannel = (id: number, ch: ChannelKey) =>
    setState((p) => ({
      ...p,
      [id]: {
        ...p[id],
        channels: { ...p[id].channels, [ch]: !p[id].channels[ch] },
      },
    }));

  const toggleAllInCategory = (cat: EventCategory, enable: boolean) => {
    setState((p) => {
      const next = { ...p };
      cat.events.forEach((e) => {
        next[e.id] = {
          enabled: enable,
          channels: { sms: enable, email: enable },
        };
      });
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {eventCategories.map((cat) => {
        const isOpen = openCats[cat.code] ?? false;
        const enabledCount = cat.events.filter(
          (e) => state[e.id]?.enabled,
        ).length;

        return (
          <Collapsible
            key={cat.code}
            open={isOpen}
            onOpenChange={() => toggleCat(cat.code)}
          >
            <Card className="border border-border">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer py-3 px-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isOpen ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className="text-muted-foreground">{cat.icon}</span>
                      <CardTitle className="text-sm font-semibold">
                        {cat.label}
                      </CardTitle>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">
                        {enabledCount}/{cat.events.length} active
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAllInCategory(
                            cat,
                            enabledCount < cat.events.length,
                          );
                        }}
                      >
                        {enabledCount < cat.events.length
                          ? "Enable All"
                          : "Disable All"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="w-16 text-xs">S.No</TableHead>
                        <TableHead className="text-xs">Event Name</TableHead>
                        <TableHead className="w-20 text-center text-xs">
                          Active
                        </TableHead>
                        <TableHead className="w-20 text-center text-xs">
                          <div className="flex flex-col items-center gap-0.5">
                            <Mail className="w-3.5 h-3.5" />
                            <span>Email</span>
                          </div>
                        </TableHead>
                        <TableHead className="w-20 text-center text-xs">
                          <div className="flex flex-col items-center gap-0.5">
                            <MessageSquare className="w-3.5 h-3.5" />
                            <span>SMS</span>
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cat.events.map((evt, idx) => {
                        const s = state[evt.id];
                        return (
                          <TableRow
                            key={evt.id}
                            className={!s?.enabled ? "opacity-50" : ""}
                          >
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {idx + 1}
                            </TableCell>
                            <TableCell className="text-sm">
                              {evt.name}
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch
                                checked={s?.enabled}
                                onCheckedChange={() => toggleEvent(evt.id)}
                                className="mx-auto"
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={s?.channels.email}
                                onCheckedChange={() =>
                                  toggleChannel(evt.id, "email")
                                }
                                disabled={!s?.enabled}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={s?.channels.sms}
                                onCheckedChange={() =>
                                  toggleChannel(evt.id, "sms")
                                }
                                disabled={!s?.enabled}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        );
      })}
    </div>
  );
};

// ── Main page ──

const NotificationSettings = () => {
  const [adminState, setAdminState] = useState(buildInitialState);
  const [consumerState, setConsumerState] = useState(buildInitialState);
  const [consumerSearch, setConsumerSearch] = useState("");
  const [selectedConsumer, setSelectedConsumer] = useState<{
    id: string;
    type: "consumer" | "meter";
  } | null>(null);

  // Admin contact details
  const [adminEmail, setAdminEmail] = useState("ops-alerts@utility.com");
  const [adminPhone, setAdminPhone] = useState("+91-9876543210");

  // Consumer contact details (loaded per consumer)
  const [consumerEmail, setConsumerEmail] = useState("");
  const [consumerPhone, setConsumerPhone] = useState("");

  // Mock consumer lookup
  const handleConsumerSearch = () => {
    if (!consumerSearch.trim()) return;
    setSelectedConsumer({
      id: consumerSearch.trim(),
      type: consumerSearch.startsWith("M") ? "meter" : "consumer",
    });
    // Mock loaded contact details
    setConsumerEmail("consumer@example.com");
    setConsumerPhone("+91-9000012345");
    toast.info(`Loaded notification settings for ${consumerSearch.trim()}`);
  };

  const handleSave = (target: "admin" | "consumer") => {
    toast.success(
      target === "admin"
        ? "Utility admin notification preferences saved"
        : `Consumer notification preferences saved for ${selectedConsumer?.id}`,
    );
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-5 h-5 text-primary" />
            <h2 className="font-heading font-semibold text-lg text-foreground">
              Notification & Alert Settings
            </h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Configure which smart meter events trigger notifications and select
            delivery channels per event.
          </p>
        </div>

        <Tabs defaultValue="admin" className="w-full">
          <TabsList className="inline-flex gap-6 mb-6 h-auto p-0 bg-transparent border-b border-border rounded-none w-full">
            <TabsTrigger
              value="admin"
              className="flex items-center gap-2 py-3 px-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent text-muted-foreground hover:text-foreground"
            >
              <Building2 className="w-4 h-4" />
              <span>Utility Admin Notifications</span>
            </TabsTrigger>
            <TabsTrigger
              value="consumer"
              className="flex items-center gap-2 py-3 px-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent text-muted-foreground hover:text-foreground"
            >
              <User className="w-4 h-4" />
              <span>Consumer Notifications</span>
            </TabsTrigger>
          </TabsList>

          {/* ── Utility Admin Tab ── */}
          <TabsContent value="admin">
            <Card className="mb-4 border-primary/20 bg-primary/5">
              <CardContent className="py-3 px-4">
                <CardDescription className="text-sm text-foreground">
                  These notification preferences apply to the{" "}
                  <strong>utility control center</strong>. All enabled events
                  will be pushed to the selected channels for operations staff.
                </CardDescription>
              </CardContent>
            </Card>

            {/* Admin Contact Details */}
            <Card className="mb-4">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Notification Recipients
                </CardTitle>
                <CardDescription className="text-xs">
                  Alerts will be delivered to these contact details for all
                  utility admin notifications.
                </CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        className="pl-9"
                        placeholder="ops-alerts@utility.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">
                      SMS Mobile Number
                    </Label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="tel"
                        value={adminPhone}
                        onChange={(e) => setAdminPhone(e.target.value)}
                        className="pl-9"
                        placeholder="+91-9876543210"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <EventConfigSection state={adminState} setState={setAdminState} />

            <div className="flex justify-end mt-6">
              <Button onClick={() => handleSave("admin")} className="gap-2">
                <Save className="w-4 h-4" />
                Save Admin Preferences
              </Button>
            </div>
          </TabsContent>

          {/* ── Consumer Tab ── */}
          <TabsContent value="consumer">
            <Card className="mb-4 border-primary/20 bg-primary/5">
              <CardContent className="py-3 px-4">
                <CardDescription className="text-sm text-foreground">
                  Configure per-consumer notification preferences. Search by{" "}
                  <strong>Consumer ID</strong> or <strong>Meter ID</strong> to
                  load and customize their alert channels.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="mb-4">
              <CardContent className="py-4 px-4">
                <div className="flex items-end gap-3">
                  <div className="flex-1 max-w-sm">
                    <Label className="text-xs text-muted-foreground mb-1 block">
                      Consumer ID / Meter ID
                    </Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="e.g. CUST-10042 or M-300187"
                        value={consumerSearch}
                        onChange={(e) => setConsumerSearch(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleConsumerSearch()
                        }
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleConsumerSearch}
                    className="gap-2"
                  >
                    <Search className="w-4 h-4" />
                    Load
                  </Button>
                </div>

                {selectedConsumer && (
                  <div className="mt-3 flex items-center gap-2">
                    <Badge variant="secondary">
                      {selectedConsumer.type === "meter" ? "Meter" : "Consumer"}
                      : {selectedConsumer.id}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      Notification preferences loaded
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Consumer Contact Details */}
            {selectedConsumer && (
              <Card className="mb-4">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    Consumer Contact Details
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Update the registered email and mobile number for this
                    consumer. Alerts will be sent to these contacts.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Registered Email
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="email"
                          value={consumerEmail}
                          onChange={(e) => setConsumerEmail(e.target.value)}
                          className="pl-9"
                          placeholder="consumer@example.com"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Registered Mobile Number
                      </Label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="tel"
                          value={consumerPhone}
                          onChange={(e) => setConsumerPhone(e.target.value)}
                          className="pl-9"
                          placeholder="+91-9000012345"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedConsumer ? (
              <>
                <EventConfigSection
                  state={consumerState}
                  setState={setConsumerState}
                />
                <div className="flex justify-end mt-6">
                  <Button
                    onClick={() => handleSave("consumer")}
                    className="gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Consumer Preferences
                  </Button>
                </div>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <User className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="text-muted-foreground text-sm">
                    Search for a Consumer ID or Meter ID above to configure
                    their notification preferences.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default NotificationSettings;
